"use client";

import { c as _c } from "react/compiler-runtime";
import { useStream } from "@langchain/langgraph-sdk/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatResponseStatus } from "@/components/ai-chat/types";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { invalidateAskDecodaHistoryReads } from "@/hooks/ask-decoda/ask-decoda-history-query";
import { applyAskDecodaCustomEvent, cancelAskDecodaPresentation, getCachedHistory, reconcileAskDecodaRun } from "@/hooks/ask-decoda/ask-decoda-live-cache";
import { type ResponseBlockBarrier, captureResponseBlockBarrier, reconcileResponseBlocks, responseBlocksText, retainUncommittedBlockPlacements } from "@/hooks/ask-decoda/ask-decoda-response-blocks";
import { type AskDecodaState, createAskDecodaChatTransport } from "@/hooks/ask-decoda/langgraph-transport";
import { patchHistoryEntry } from "@/lib/ask-decoda-history-cache";
import { AskDecodaCustomEventType } from "@/types/chat-thread";
import { AskDecodaOperationStatus } from "@/types/chat-thread";
import type { AskDecodaStreamEvent } from "@/types/chat-thread";
import { AskDecodaRunKind } from "@/stores/ask-decoda-store";
import { type AskDecodaLiveRun, registerAskDecodaStreamAbort, useAskDecodaStore } from "@/stores/ask-decoda-store";
const REATTACH_DELAYS = [500, 1000, 2000, 4000, 8000];
const TERMINAL_OPERATIONS = new Set<AskDecodaOperationStatus>([AskDecodaOperationStatus.COMPLETED, AskDecodaOperationStatus.FAILED, AskDecodaOperationStatus.CANCELLED]);

/** One operation per conversation, owned by the application host, not a page.
 * Each socket gets a fresh SDK accumulator; reconnect never resends a mutation.
 */
export function AskDecodaStreamRunner(t0) {
  const $ = _c(23);
  const {
    run,
    tenantSubDomain
  } = t0;
  const {
    tenant
  } = useTenant();
  const currentTenantSubDomain = tenant?.subDomain;
  const [subscription, setSubscription] = useState(0);
  const [detached, setDetached] = useState(false);
  const accepted = useRef(false);
  const consecutiveFailures = useRef(0);
  const timer = useRef(undefined);
  let t1;
  if ($[0] !== currentTenantSubDomain || $[1] !== run || $[2] !== tenantSubDomain) {
    t1 = () => {
      const state = useAskDecodaStore.getState();
      return Boolean(currentTenantSubDomain) && run.tenantSubDomain === currentTenantSubDomain && run.tenantSubDomain === tenantSubDomain && state.runtimeEpoch === run.runtimeEpoch && state.liveRunsByThreadId[run.threadId]?.runId === run.runId;
    };
    $[0] = currentTenantSubDomain;
    $[1] = run;
    $[2] = tenantSubDomain;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isCurrent = t1;
  let t2;
  if ($[4] !== isCurrent || $[5] !== run) {
    t2 = () => {
      if (!isCurrent()) {
        return;
      }
      consecutiveFailures.current = 0;
      if (accepted.current) {
        return;
      }
      accepted.current = true;
      run.resolveAccepted?.();
      run.onAccepted?.();
    };
    $[4] = isCurrent;
    $[5] = run;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onAccepted = t2;
  let t3;
  if ($[7] !== isCurrent) {
    t3 = () => {
      if (!isCurrent()) {
        return;
      }
      const delay = REATTACH_DELAYS[consecutiveFailures.current];
      if (delay === undefined) {
        setDetached(true);
        return;
      }
      consecutiveFailures.current = consecutiveFailures.current + 1;
      timer.current = setTimeout(() => {
        if (isCurrent()) {
          setSubscription(_temp);
        }
      }, delay);
    };
    $[7] = isCurrent;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onDetached = t3;
  let t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => clearTimeout(timer.current);
    t5 = [];
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[11] !== detached || $[12] !== isCurrent) {
    t6 = () => {
      const reconnect = () => {
        if (detached && isCurrent()) {
          consecutiveFailures.current = 0;
          setDetached(false);
          setSubscription(_temp2);
        }
      };
      window.addEventListener("online", reconnect);
      return () => window.removeEventListener("online", reconnect);
    };
    t7 = [detached, isCurrent];
    $[11] = detached;
    $[12] = isCurrent;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  if (!currentTenantSubDomain || run.tenantSubDomain !== currentTenantSubDomain || run.tenantSubDomain !== tenantSubDomain) {
    return null;
  }
  const t8 = subscription === 0;
  let t9;
  if ($[15] !== isCurrent || $[16] !== onAccepted || $[17] !== onDetached || $[18] !== run || $[19] !== subscription || $[20] !== t8 || $[21] !== tenantSubDomain) {
    t9 = <AskDecodaSubscription key={subscription} run={run} tenantSubDomain={tenantSubDomain} initial={t8} isCurrentRun={isCurrent} onAccepted={onAccepted} onDetached={onDetached} />;
    $[15] = isCurrent;
    $[16] = onAccepted;
    $[17] = onDetached;
    $[18] = run;
    $[19] = subscription;
    $[20] = t8;
    $[21] = tenantSubDomain;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}

/** A fresh SDK accumulator per subscription. Checkpoints advance canonical
 * blocks and each segment's consumed SDK prefix; new tokens stay provisional.
 */
function _temp2(current_0) {
  return current_0 + 1;
}
function _temp(current) {
  return current + 1;
}
function AskDecodaSubscription({
  run,
  tenantSubDomain,
  initial,
  isCurrentRun,
  onAccepted,
  onDetached
}: {
  run: AskDecodaLiveRun;
  tenantSubDomain: string | undefined;
  initial: boolean;
  isCurrentRun: () => boolean;
  onAccepted: () => void;
  onDetached: () => void;
}) {
  const acceptedHandler = useRef(onAccepted);
  acceptedHandler.current = onAccepted;
  const transport = useMemo(() => createAskDecodaChatTransport(() => acceptedHandler.current()), []);
  const responseChatId = run.responseChatId ?? run.chatId;
  const disposed = useRef(false);
  const snapshotBarrier = useRef<ResponseBlockBarrier | null>(null);
  const blockPlacements = useRef(new Map<string, string | null>());
  const terminal = useRef(false);
  const revision = useRef(0);
  const lastError = useRef<unknown>(undefined);
  const isCurrent = useCallback(() => !disposed.current && isCurrentRun(), [isCurrentRun]);
  const onError = useCallback((error: unknown) => {
    if (isCurrent()) lastError.current = error;
  }, [isCurrent]);
  const clearProgress = useCallback(() => {
    if (!isCurrent()) return;
    patchHistoryEntry(queryClient, tenantSubDomain, run.threadId, {
      id: responseChatId
    }, entry => entry.progressMessage === undefined ? entry : {
      ...entry,
      progressMessage: undefined
    });
  }, [isCurrent, responseChatId, run.threadId, tenantSubDomain]);
  const settlement = useRef<Promise<void> | null>(null);
  const finishOperation = useCallback(() => {
    if (settlement.current) return settlement.current;
    settlement.current = Promise.resolve();
    if (!isCurrent()) return settlement.current;
    const before = revision.current;
    // The terminal custom event has already committed its authoritative cache
    // projection. REST history AND thread-list refresh are optional reads, not
    // admission barriers: either can be slow while the approval is ready.
    useAskDecodaStore.getState().finishStopping(run.threadId, run.chatId);
    useAskDecodaStore.getState().finishLiveRun(run.threadId, run.runId);
    const attachContinuation = () => {
      const state = useAskDecodaStore.getState();
      if (state.runtimeEpoch !== run.runtimeEpoch || state.liveRunsByThreadId[run.threadId]) return;
      const entry_0 = getCachedHistory(tenantSubDomain, run.threadId).find(item => item.id === responseChatId && item.threadId === run.threadId);
      // A requested root operation can be settled while its response belongs
      // to a newer running continuation. History's reattach effect may already
      // have observed that identity while this old run still held admission.
      const continuationChatId = entry_0?.status === ChatResponseStatus.STREAMING && entry_0.operationChatId && entry_0.operationChatId !== run.chatId ? entry_0.operationChatId : undefined;
      if (continuationChatId) {
        // No await between release and attach: never replace a newer run installed by a
        // synchronous store observer, and never resend a send/resume mutation.
        invalidateAskDecodaHistoryReads({
          queryClient,
          tenantSubDomain,
          threadId: run.threadId
        });
        state.startReattachRun({
          tenantSubDomain: run.tenantSubDomain,
          threadId: run.threadId,
          chatId: continuationChatId,
          responseChatId
        });
      }
    };
    attachContinuation();
    const state_0 = useAskDecodaStore.getState();
    if (state_0.runtimeEpoch !== run.runtimeEpoch || state_0.liveRunsByThreadId[run.threadId]) return settlement.current;
    // Ownership stays retired once ANY newer admission wins, even if that
    // operation also finishes before this optional read returns. Deliberate
    // unmount after settlement must not invalidate an otherwise current read.
    let superseded = false;
    const unsubscribe = useAskDecodaStore.subscribe(next => {
      if (next.runtimeEpoch !== run.runtimeEpoch || next.liveRunsByThreadId[run.threadId]) {
        superseded = true;
        unsubscribe();
      }
    });
    const isSettledCurrent = () => !superseded && useAskDecodaStore.getState().runtimeEpoch === run.runtimeEpoch && !useAskDecodaStore.getState().liveRunsByThreadId[run.threadId] && revision.current === before;
    // Reconcile only after authoritative settlement, never on disconnect.
    // Its existing history revision/admission checks still guard cache commits.
    void reconcileAskDecodaRun({
      tenantSubDomain,
      threadId: run.threadId,
      createdDate: run.createdDate,
      runtimeEpoch: run.runtimeEpoch,
      isCurrent: isSettledCurrent
    }).catch(() => undefined).then(() => {
      if (isSettledCurrent()) attachContinuation();
    }).finally(unsubscribe);
    return settlement.current;
  }, [isCurrent, responseChatId, run, tenantSubDomain]);
  const patchStreamedBlocks = useCallback(() => {
    const barrier = snapshotBarrier.current;
    if (!isCurrent() || terminal.current || !barrier) return;
    const responseBlocks = reconcileResponseBlocks(barrier, streamRef.current.messages, blockPlacements.current);
    const answer = responseBlocksText(responseBlocks);
    patchHistoryEntry(queryClient, tenantSubDomain, run.threadId, {
      id: responseChatId
    }, entry_1 => {
      const previous = entry_1.responseBlocks ?? [];
      const unchanged = entry_1.responseBlocks != null && previous.length === responseBlocks.length && responseBlocks.every((block, index) => {
        const before_0 = previous[index];
        return before_0.type === block.type && before_0.id === block.id && (block.type === "text" ? before_0.type === "text" && before_0.text === block.text : before_0.type === "execution" && before_0.executionId === block.executionId);
      });
      return unchanged && entry_1.answer === answer ? entry_1 : {
        ...entry_1,
        answer,
        responseBlocks
      };
    });
  }, [isCurrent, responseChatId, run.threadId, tenantSubDomain]);
  const stream = useStream<AskDecodaState, {
    CustomEventType: AskDecodaStreamEvent;
  }>({
    transport,
    threadId: run.threadId,
    initialValues: {
      messages: []
    },
    onError,
    onCustomEvent: event => {
      if (!isCurrent() || terminal.current) return;
      if (event.operationChatId !== run.chatId || event.responseChatId !== responseChatId) return;
      if (event.type === AskDecodaCustomEventType.TEXT_BLOCK_START) {
        // The server supplies placement, never a browser timestamp. Repeated
        // starts cannot move an existing block or change its ownership.
        if (!blockPlacements.current.has(event.blockId)) {
          blockPlacements.current.set(event.blockId, event.afterBlockId);
        }
        patchStreamedBlocks();
        return;
      }
      if (event.type === AskDecodaCustomEventType.METADATA || event.type === AskDecodaCustomEventType.HISTORY_SNAPSHOT) {
        const entry_2 = event.type === AskDecodaCustomEventType.METADATA ? event.chatHistory : event.entry;
        if (event.operationChatId !== run.chatId || event.responseChatId !== responseChatId || entry_2.id !== responseChatId || entry_2.threadId !== run.threadId) return;
        revision.current += 1;
        // This SDK getter is live before React renders preceding chunks. Only
        // committed segments consume prefixes; canonical sanitation may change
        // their lengths, and another segment can still be uncommitted.
        const blocks = entry_2.responseBlocks ?? [];
        snapshotBarrier.current = captureResponseBlockBarrier(blocks, streamRef.current.messages);
        blockPlacements.current = retainUncommittedBlockPlacements(blocks, blockPlacements.current);
        terminal.current = TERMINAL_OPERATIONS.has(event.operationStatus);
        onAccepted();
      }
      applyAskDecodaCustomEvent({
        tenantSubDomain,
        threadId: run.threadId,
        chatId: responseChatId,
        event
      });
      const presented = getCachedHistory(tenantSubDomain, run.threadId).find(entry_3 => entry_3.id === responseChatId);
      if (!terminal.current && presented?.status === ChatResponseStatus.CANCELLED) {
        // CANCEL_REQUESTED is not durable execution settlement. It does end
        // browser presentation: dispose without terminal reconciliation/replay.
        cancelAskDecodaPresentation({
          tenantSubDomain,
          threadId: run.threadId,
          responseChatId,
          operationChatId: run.chatId,
          entryOperationId: presented.operationChatId ?? presented.id,
          runId: run.runId,
          runtimeEpoch: run.runtimeEpoch
        });
        return;
      }
      if (terminal.current) void finishOperation();else patchStreamedBlocks();
    }
  });
  const streamRef = useRef(stream);
  streamRef.current = stream;
  useEffect(() => registerAskDecodaStreamAbort(run.threadId, () => {
    // Local stop only detaches; explicit cancel is a separate server request.
    clearProgress();
    disposed.current = true;
    void streamRef.current.stop();
  }), [clearProgress, run.threadId]);
  useEffect(patchStreamedBlocks, [patchStreamedBlocks, stream.messages]);
  useEffect(() => {
    let active = true;
    let started = false;
    disposed.current = false;
    // StrictMode's discarded setup never submits a mutation.
    void Promise.resolve().then(async () => {
      if (!active || !isCurrent()) return;
      clearProgress();
      started = true;
      try {
        const resume = run.resume;
        if (initial && run.kind === AskDecodaRunKind.RESUME && !resume) throw new Error("Approval details are unavailable. Reload the conversation.");
        await streamRef.current.submit(initial && run.payload ? {
          message: run.payload.message,
          pinIntent: run.payload.pinIntent,
          currentPage: run.payload.currentPage,
          currentSettingsResource: run.payload.currentSettingsResource,
          setupItemId: run.payload.setupItemId,
          timezone: run.payload.timezone,
          files: run.payload.files,
          chatId: run.chatId
        } : null, {
          config: {
            configurable: {
              thread_id: run.threadId
            }
          },
          ...(initial && run.kind === AskDecodaRunKind.RESUME && resume ? {
            command: {
              resume: {
                interrupt_id: resume.interruptId,
                checkpoint_id: resume.checkpointId,
                decisions: resume.decisions
              }
            }
          } : {}),
          ...(!initial || run.kind !== AskDecodaRunKind.SEND ? {
            context: {
              chatId: run.chatId,
              responseChatId
            }
          } : {})
        });
      } catch (error_0) {
        onError(error_0);
      }
      if (!active || !isCurrent()) return;
      // SDK submit resolves even when onError ran. Neither EOF nor that promise
      // is operation settlement; only a journal status in a snapshot is.
      const error_1 = lastError.current as {
        response?: {
          status?: number;
        };
      } | undefined;
      const status = error_1?.response?.status;
      const rejected = !snapshotBarrier.current && (status === 404 || initial && run.kind !== AskDecodaRunKind.REATTACH && [400, 401, 403, 409, 422, 429].includes(status ?? 0));
      if (rejected) {
        run.rejectFinished?.(lastError.current);
        if (run.kind === AskDecodaRunKind.SEND) {
          patchHistoryEntry(queryClient, tenantSubDomain, run.threadId, {
            id: responseChatId
          }, entry_4 => ({
            ...entry_4,
            status: ChatResponseStatus.FAILED
          }));
        }
      }
      if (rejected) {
        // A rejected admission (or authoritative not-found on attach) has no
        // operation to settle. Release local busy state immediately; an
        // unrelated slow/failing history read must not leave approval spinning.
        // Ambiguous network/5xx failures still attach without resending effects.
        useAskDecodaStore.getState().finishLiveRun(run.threadId, run.runId);
        void reconcileAskDecodaRun({
          tenantSubDomain,
          threadId: run.threadId,
          createdDate: run.createdDate,
          runtimeEpoch: run.runtimeEpoch
        }).catch(() => undefined);
      } else if (terminal.current) {
        await finishOperation();
      } else {
        clearProgress();
        onDetached();
      }
    });
    return () => {
      active = false;
      clearProgress();
      disposed.current = true;
      if (started) void streamRef.current.stop();
    };
  }, [clearProgress, finishOperation, initial, isCurrent, onDetached, onError, responseChatId, run, tenantSubDomain]);
  return null;
}